/**
 * AdLens FX — Rule Engine
 *
 * Evaluates Knowledge Base requirements against a captured request and
 * returns a check group compatible with validators.jsx's runValidations() output.
 *
 * Design:
 *   - KB requirements drive the checks — no AI calls at QA time
 *   - Each requirement has optional `parameter` (dot-path) and `expectedPattern`/`expectedValue`
 *   - Parameter extractors translate request shapes → flat { key: value } maps
 *   - Results plug in as a "Knowledge Base Requirements" group in runValidations()
 *
 * Usage:
 *   import { runKBRules } from './RuleEngine';
 *   const group = runKBRules(req, requestType);   // { title, scope, results[] }
 */

// NOTE: these are all call-time reads over an in-memory cache that the proxy
// populates via `loadKnowledge()` (see src/knowledge/index.js) — this module
// assumes `loadKnowledge()` was already kicked off at app boot (App.js). Do
// NOT make this module's exports async; if the cache hasn't resolved yet,
// `getRequirementsFor`/`getRecommendations` simply degrade to [] rather than
// blocking, which is an accepted small race (see src/knowledge/index.js).
import { getRequirementsFor, getRecommendations, KNOWLEDGE_BASE } from '../knowledge';

// ─── Utilities ────────────────────────────────────────────────────────────────

/** Parse URL query string into flat { key: value } object */
function parseQS(url = '') {
  try {
    const qsStart = url.indexOf('?');
    if (qsStart < 0) return {};
    const qs = url.slice(qsStart + 1);
    const out = {};
    for (const pair of qs.split('&')) {
      const eq = pair.indexOf('=');
      if (eq < 0) continue;
      const k = decodeURIComponent(pair.slice(0, eq).replace(/\+/g, ' '));
      const v = decodeURIComponent(pair.slice(eq + 1).replace(/\+/g, ' '));
      if (out[k] !== undefined) {
        out[k] = Array.isArray(out[k]) ? [...out[k], v] : [out[k], v];
      } else {
        out[k] = v;
      }
    }
    return out;
  } catch {
    return {};
  }
}

/** Parse a kv-string like "key1=val1&key2=val2" (cust_params style) */
function parseKV(s = '') {
  const out = {};
  try {
    for (const pair of String(s).split('&')) {
      const eq = pair.indexOf('=');
      if (eq < 0) continue;
      const k = decodeURIComponent(pair.slice(0, eq).replace(/\+/g, ' '));
      const v = decodeURIComponent(pair.slice(eq + 1).replace(/\+/g, ' '));
      out[k] = v;
    }
  } catch {}
  return out;
}

/** Safely parse JSON body, returning null on failure.
 *  Checks requestBodyParsed first (proxy pre-parses gzip/binary bodies),
 *  then falls back to the raw requestBody string. */
function tryParseBody(req) {
  // requestBodyParsed is set by the proxy when it pre-parses a gzipped or
  // binary-encoded body (common for APS and Prebid QUIC requests).
  const body = req?.requestBodyParsed || req?.requestBody || req?.body || req?.validData?.raw;
  if (!body) return null;
  try {
    return typeof body === 'object' ? body : JSON.parse(body);
  } catch {
    return null;
  }
}

/**
 * Resolve a dot-path like "imp[0].ext.prebid.storedrequest.id" or
 * "cust_params.amzn_b" against an object, returning the value or undefined.
 */
function resolvePath(obj, path) {
  if (!obj || !path) return undefined;
  // Normalise "imp[0].foo" → "imp.0.foo"
  const parts = path.replace(/\[(\d+)\]/g, '.$1').split('.');
  let cur = obj;
  for (const part of parts) {
    if (cur === null || cur === undefined) return undefined;
    if (Array.isArray(cur)) {
      // If part is a numeric index, use it; otherwise check first element
      const idx = Number(part);
      cur = isNaN(idx) ? cur[0]?.[part] : cur[idx];
    } else {
      cur = cur[part];
    }
  }
  return cur;
}

// ─── Parameter Extractors ────────────────────────────────────────────────────
// Each extractor returns a flat { paramPath: value } map for the request type.
// 'cust_params.*' sub-keys are flattened so the KB path 'cust_params.amzn_b' works.

function extractGAMParams(req) {
  const qs = parseQS(req?.url || '');
  const custRaw = qs['cust_params'] || '';
  const cust = parseKV(custRaw);
  // Flatten cust_params.* sub-keys
  const params = { ...qs };
  for (const [k, v] of Object.entries(cust)) {
    params[`cust_params.${k}`] = v;
  }
  return params;
}

function extractIMAParams(req) {
  // IMA registers via a POST to pubads.g.doubleclick.net/ssai/
  // Parameters are in the URL plus validData from the proxy validator
  const qs = parseQS(req?.url || '');
  const vd = req?.validData || {};
  const custRaw = qs['cust_params'] || vd.cust_params || '';
  const cust = parseKV(custRaw);
  const params = {
    ...qs,
    // Surface validData fields that the proxy already parsed
    ad_player_name: vd.ad_player_name || qs.ad_player_name,
    msid:           vd.msid || qs.msid || qs.dfp_msid,
    pmt:            vd.pmt  || qs.pmt,
    omid:           vd.omid || qs.omid,
    gdpr_consent:   qs.gdpr_consent || vd.gdpr_consent,
    us_privacy:     qs.us_privacy   || vd.us_privacy,
  };
  for (const [k, v] of Object.entries(cust)) {
    params[`cust_params.${k}`] = v;
  }
  return params;
}

function extractAmazonDisplayParams(req) {
  // APS display requests are JSON to ad.amazon.com/ad/apstag/mobileapp
  // The proxy may pre-parse the body into requestBodyParsed (tryParseBody handles this).
  // validData is also checked as a fallback — the proxy extracts slot UUIDs and appId
  // into validData even when the raw body isn't captured.
  const body = tryParseBody(req);
  const vd = req?.validData || {};

  // Slot UUID: check body first, then validData (proxy extracts slot info into validData)
  const slotFromBody = body?.slots?.[0]?.slot;
  const slotFromVd   = vd.slots?.[0]?.slot || vd.slot || vd.slotUuid;

  // SKAdNetwork: body or validData — Android requests legitimately have no skadn
  const skadnFromBody = body?.skadn;
  const skadnFromVd   = vd.skadn;

  const params = {
    appId:               body?.appId                              || vd.appId,
    'pj.mediationName':  body?.pj?.mediationName || body?.pj?.['mediationName'] || vd.mediationName,
    adsdk:               body?.adsdk                             || vd.adsdk,
    'slots[0].slot':     slotFromBody                            || slotFromVd,
    skadn:               skadnFromBody                           || skadnFromVd,
    gdpr_consent:        body?.gdpr_consent                      || vd.gdpr_consent,
    us_privacy:          body?.us_privacy                        || vd.us_privacy,
  };
  return params;
}

function extractAmazonVideoParams(req) {
  // APS OTT C2S is an OpenRTB 2.5 POST to aax-ott-c2s.amazon-adsystem.com
  const body = tryParseBody(req);
  return {
    'imp[0]':           body?.imp?.[0],
    'app.bundle':       body?.app?.bundle,
    'app.publisher.id': body?.app?.publisher?.id,
    'device.ua':        body?.device?.ua,
    'user.id':          body?.user?.id,
    appId:              body?.app?.ext?.appId || body?.app?.bundle,
  };
}

function extractPrebidParams(req) {
  const body = tryParseBody(req);
  return {
    'imp[0].ext.prebid.storedrequest.id':
        body?.imp?.[0]?.ext?.prebid?.storedrequest?.id,
    'app.bundle':           body?.app?.bundle,
    'app.publisher.id':     body?.app?.publisher?.id,
    'regs.ext.us_privacy':  body?.regs?.ext?.us_privacy || body?.regs?.us_privacy,
    'regs.ext.gdpr':        body?.regs?.ext?.gdpr,
    'device.ua':            body?.device?.ua,
    'device.ifa':           body?.device?.ifa,
    'source.schain':        body?.source?.schain,
    gdpr_consent:           body?.user?.ext?.consent,
    us_privacy:             body?.regs?.ext?.us_privacy,
  };
}

function extractSpringServeParams(req) {
  // SpringServe VAST/tracking — most checks are lifecycle-level, not param-level
  const qs = parseQS(req?.url || '');
  const vd = req?.validData || {};
  return {
    ...qs,
    event:   vd.event,
    bpa:     qs.bpa  || vd.bpa,
    adomain: qs.adomain || vd.adomain,
    gam_ad_unit: qs.gam_ad_unit || vd.gam_ad_unit,
  };
}

/** Dispatcher: picks the right extractor for the request type */
function extractParams(req, requestType) {
  switch (requestType) {
    case 'google':
      return extractGAMParams(req);
    // Impression/click beacons are NOT auction requests — running GAM param checks
    // against them generates false failures (no iu, sz, cust_params on a beacon URL).
    // Return empty so KB rules find nothing to check and produce zero results.
    case 'google_impression':
    case 'google_click':
      return {};
    case 'ima':
    case 'gam_video':
      return extractIMAParams(req);
    case 'amazon':
      return extractAmazonDisplayParams(req);
    case 'amazon_video':
      return extractAmazonVideoParams(req);
    case 'prebid':
      return extractPrebidParams(req);
    case 'springserve_vast':
    case 'vast_tracking':
      return extractSpringServeParams(req);
    default:
      return {};
  }
}

// ─── Requirement Evaluator ───────────────────────────────────────────────────

/**
 * Evaluate a single KB requirement against extracted params.
 * Returns a result object compatible with validators.jsx groups.results[].
 */
function evaluateRequirement(req, requestType, params) {
  return (requirement) => {
    const {
      id,
      name,
      severity,
      parameter,
      expectedPattern,
      expectedValue,
      validate,
      failMessage,
      passMessage,
    } = requirement;

    // Map KB severity to validator severity
    const sev = severity === 'required' ? 'fail'
              : severity === 'recommended' ? 'warn'
              : 'pass'; // 'info' → shown as pass (informational)

    // If no parameter to check, this requirement needs code-level validation
    // (e.g. "DAI stream must fire before impression"). Mark as not evaluated.
    if (!parameter) {
      return {
        id,
        label:    name,
        severity: 'pass', // don't flag as fail — just not checkable here
        message:  passMessage || `${name} — evaluated at lifecycle level`,
        _kbDriven: true,
        _skipped:  true,
      };
    }

    // Resolve the parameter value
    // For dot-paths not in the flat params map, try resolving via path on validData
    let value = params[parameter];
    if (value === undefined) {
      value = resolvePath(req?.validData, parameter);
    }
    if (value === undefined) {
      value = resolvePath(tryParseBody(req), parameter);
    }

    const missing = value === undefined || value === null || value === '';

    if (missing) {
      return {
        id,
        label:    name,
        severity: sev,
        message:  failMessage || `${name} — parameter "${parameter}" not found`,
        _kbDriven: true,
        _parameter: parameter,
      };
    }

    // Pattern check
    if (expectedPattern) {
      const strVal = Array.isArray(value) ? value[0] : String(value);
      try {
        if (!new RegExp(expectedPattern).test(strVal)) {
          return {
            id,
            label:    name,
            severity: sev,
            message:  failMessage || `${name} — "${strVal}" does not match expected pattern`,
            _kbDriven: true,
            _parameter: parameter,
            _value: strVal,
          };
        }
      } catch { /* invalid regex in KB — skip pattern check */ }
    }

    // Exact value check
    if (expectedValue !== undefined) {
      const strVal = Array.isArray(value) ? value[0] : String(value);
      if (strVal !== String(expectedValue)) {
        return {
          id,
          label:    name,
          severity: sev,
          message:  failMessage || `${name} — expected "${expectedValue}", got "${strVal}"`,
          _kbDriven: true,
          _parameter: parameter,
          _value: strVal,
        };
      }
    }

    // Structured validator block (`validate: { kind, ... }`).
    //
    // 72 of the 267 knowledge-pack requirements carry one of these, and until
    // now NONE of them ran: this function only ever read the singular
    // `expectedPattern` / `expectedValue` fields, so a requirement whose only
    // constraint lived in `validate` degraded to a presence check and passed
    // any value at all. The conformance engine validates the same blocks via
    // knowledgeSemantics.js, which is how the divergence surfaced —
    // `output=vast99`, `is_lat=2` and an out-of-grid `hb_pb` all passed here
    // while failing there.
    //
    // `decode` and `bannerFormat` are deliberately NOT evaluated: they need a
    // decoder and a size-grammar parser this module does not have, and a
    // half-implemented check is worse than an honest skip. They stay
    // presence-only, as they were.
    if (validate && typeof validate === 'object') {
      const strVal = Array.isArray(value) ? value[0] : String(value);
      // Deliberately does NOT fall back to the requirement's `failMessage`.
      // Packs authored that string for the parameter-MISSING case, which was
      // the only failure this engine could produce before the validate block
      // ran — gma_rdp's reads "rdp= absent — acceptable (defaults to 0…)".
      // Reusing it for an enum violation prints "absent" about a parameter
      // that is present with an illegal value. The generated message names the
      // actual violation instead.
      const fail = (msg) => ({
        id,
        label:    name,
        severity: sev,
        message:  msg,
        _kbDriven: true,
        _parameter: parameter,
        _value: strVal,
      });

      switch (validate.kind) {
        case 'enum': {
          const values = Array.isArray(validate.values) ? validate.values : [];
          if (values.length && !values.some((v) => String(v) === strVal)) {
            return fail(`${name} — "${strVal}" is not one of ${values.join(', ')}`);
          }
          break;
        }
        case 'regex': {
          if (validate.pattern) {
            try {
              if (!new RegExp(validate.pattern).test(strVal)) {
                return fail(`${name} — "${strVal}" does not match ${validate.pattern}`);
              }
            } catch { /* invalid regex in the pack — skip, same as expectedPattern above */ }
          }
          break;
        }
        case 'type': {
          // Only `number`/`integer` are checkable here — every URL param
          // arrives as a string, so a `string` type assertion is vacuous.
          if (validate.type === 'number' || validate.type === 'integer') {
            const n = Number(strVal);
            if (!Number.isFinite(n) || (validate.type === 'integer' && !Number.isInteger(n))) {
              return fail(`${name} — "${strVal}" is not ${validate.type === 'integer' ? 'an integer' : 'a number'}`);
            }
          }
          break;
        }
        case 'range': {
          const n = Number(strVal);
          if (!Number.isFinite(n)) {
            return fail(`${name} — "${strVal}" is not numeric, so it cannot be range-checked`);
          }
          const { min, max } = validate;
          if ((min != null && n < min) || (max != null && n > max)) {
            return fail(`${name} — ${n} is outside [${min ?? '-∞'}..${max ?? '∞'}]`);
          }
          break;
        }
        default:
          // decode / bannerFormat / anything new — presence-only, see above.
          break;
      }
    }

    return {
      id,
      label:    name,
      severity: 'pass',
      message:  passMessage || `${name} — ${parameter} present`,
      _kbDriven: true,
      _parameter: parameter,
      _value: Array.isArray(value) ? value.join(', ') : String(value).slice(0, 80),
    };
  };
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Run all KB requirements applicable to the given request type.
 *
 * @param {object} req         — the request row from the validator table
 * @param {string} requestType — e.g. 'google', 'prebid', 'amazon', 'ima'
 * @returns {{ title, scope, results[], _kbDriven } | null}
 *   Returns null if no KB requirements apply to this request type.
 */
export function runKBRules(req, requestType) {
  const requirements = getRequirementsFor(requestType);
  if (!requirements.length) return null;

  const params  = extractParams(req, requestType);
  const evalReq = evaluateRequirement(req, requestType, params);

  // Only include requirements that have a checkable parameter
  // (lifecycle-level checks without a parameter are handled elsewhere)
  const results = requirements
    .filter(r => r.parameter) // only param-checkable ones here
    .map(evalReq)
    .filter(r => !r._skipped); // remove skipped informational ones

  if (!results.length) return null;

  return {
    title:     'Knowledge Base Requirements',
    scope:     'KB',
    results,
    _kbDriven: true,
  };
}

/**
 * Run KB recommendations for a request type, returning them as a separate
 * info group (pass-level, advisory only).
 */
export function runKBRecommendations(req, requestType) {
  // Find KB entries that apply to this type
  const applicableKBs = KNOWLEDGE_BASE.filter(kb =>
    (kb.appliesTo || []).includes(requestType)
  );
  const recs = applicableKBs.flatMap(kb => getRecommendations(kb.id));
  if (!recs.length) return null;

  return {
    title:     'Recommendations',
    scope:     'KB',
    results:   recs.map(r => ({
      id:       r.id,
      label:    r.name,
      severity: 'warn',
      message:  r.description + (r.reference ? ` — ${r.reference}` : ''),
      _kbDriven: true,
      _advisory: true,
    })),
    _kbDriven: true,
  };
}

/**
 * Convenience: check a single parameter in a request's extracted params.
 * Used by VideoLifecycleView and AdLifecycleView for cross-request checks.
 *
 * @param {object} req
 * @param {string} requestType
 * @param {string} paramPath   dot-path, e.g. 'cust_params.amzn_b'
 * @returns {string|undefined}  the value, or undefined if absent
 */
export function getParamValue(req, requestType, paramPath) {
  const params = extractParams(req, requestType);
  let val = params[paramPath];
  if (val === undefined) val = resolvePath(req?.validData, paramPath);
  if (val === undefined) val = resolvePath(tryParseBody(req), paramPath);
  return val;
}
